import React, { useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Home, ArrowLeft } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import useAnalytics from '../hooks/useAnalytics';

// Popular pages data for better maintainability
const POPULAR_PAGES = [
  {
    path: '/knowledge-intelligence',
    title: 'Knowledge Intelligence',
    description: 'Explore our AI-powered knowledge management'
  },
  {
    path: '/contact',
    title: 'Contact Us',
    description: 'Get in touch with our team'
  },
  {
    path: '/glossary',
    title: 'Glossary',
    description: 'Browse our AI and tech terms'
  },
  {
    path: '/pricing',
    title: 'Pricing',
    description: 'View our plans and pricing'
  }
] as const;

const NotFound = () => {
  const analytics = useAnalytics();
  const location = useLocation();

  // Memoized analytics tracking function
  const trackNotFoundError = useCallback(() => {
    analytics.trackEvent('404_error', {
      path: location.pathname,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    });
  }, [analytics, location.pathname]);

  useEffect(() => {
    // Track 404 errors
    trackNotFoundError();

    // Update document title for better UX
    document.title = `Page Not Found | Strive`;
  }, [trackNotFoundError]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Helmet>
        <title>Page Not Found | Strive</title>
        <meta name="description" content="The page you're looking for cannot be found. Return to Strive's homepage to explore our AI-powered product operations platform." />
        <meta name="robots" content="noindex, follow" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:title" content="Page Not Found | Strive" />
        <meta property="og:description" content="The page you're looking for cannot be found. Return to Strive's homepage to explore our AI-powered product operations platform." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/images/404-social-share.jpg" />
        <meta property="og:image:alt" content="Strive 404 Page Not Found" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Page Not Found | Strive" />
        <meta name="twitter:description" content="The page you're looking for cannot be found. Return to Strive's homepage to explore our AI-powered product operations platform." />
        <meta name="twitter:image" content="/images/404-social-share.jpg" />
        <meta name="twitter:image:alt" content="Strive 404 Page Not Found" />
        
        {/* Additional meta tags */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href="https://strive.us/404" />
      </Helmet>

      <Navbar />

      <main 
        className="flex-grow flex items-center justify-center px-6 py-24 sm:py-32 lg:px-8"
        role="main"
        aria-labelledby="error-title"
      >
        <div className="text-center">
          <p className="text-base font-semibold text-blue-600" aria-hidden="true">404</p>
          <h1 
            id="error-title"
            className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl"
          >
            Page not found
          </h1>
          <p className="mt-6 text-base leading-7 text-gray-700 max-w-md mx-auto">
            Sorry, we couldn't find the page you're looking for. Let's get you back on track.
          </p>
          <div 
            className="mt-10 flex items-center justify-center gap-x-6"
            role="navigation"
            aria-label="Error page navigation"
          >
            <Link to="/">
              <Button 
                className="bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-600"
                aria-label="Return to homepage"
              >
                <Home className="mr-2 h-4 w-4" aria-hidden="true" />
                Back to home
              </Button>
            </Link>
            <Button 
              variant="outline"
              onClick={() => window.history.back()}
              className="text-gray-700 hover:text-gray-900 border-gray-300 hover:border-gray-400"
              aria-label="Go back to previous page"
            >
              <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
              Go back
            </Button>
          </div>
        </div>
      </main>

      {/* Suggested Links Section */}
      <section 
        className="pb-24 px-6"
        aria-labelledby="popular-pages-title"
      >
        <div className="max-w-2xl mx-auto text-center">
          <h2 
            id="popular-pages-title"
            className="text-lg font-semibold text-gray-900 mb-6"
          >
            Popular Pages
          </h2>
          <div 
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            role="navigation"
            aria-label="Popular pages navigation"
          >
            {POPULAR_PAGES.map((page) => (
              <Link 
                key={page.path}
                to={page.path}
                className="p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                aria-labelledby={`${page.title.toLowerCase().replace(/\s+/g, '-')}-title`}
              >
                <h3 
                  id={`${page.title.toLowerCase().replace(/\s+/g, '-')}-title`}
                  className="font-medium text-gray-900"
                >
                  {page.title}
                </h3>
                <p className="text-sm text-gray-700 mt-1">
                  {page.description}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Set display name for debugging
NotFound.displayName = 'NotFound';

export default NotFound;
